import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    AreaChart,
    Area,
    Tooltip,
    XAxis,
    YAxis,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { AiOutlineCaretDown } from "react-icons/ai";
import { getInvoices } from "../services/statisticsService"; // ✅ Replace with actual service to fetch invoices

function Sales() {
    const [salesData, setSalesData] = useState([]);
    const [view, setView] = useState("Monthly");

    useEffect(() => {
        fetchSalesData();
    }, [view]);

    // ✅ Fetch invoices and group by Daily, Weekly, Monthly
    const fetchSalesData = async () => {
        try {
            const invoicesResponse = await getInvoices(); // Call API service
            if (!invoicesResponse.success) return;

            const now = new Date();
            let groupedData = {};

            invoicesResponse.data.forEach((invoice) => {
                if (invoice.payment_status === "Completed") {
                    const invoiceDate = new Date(invoice.created_at);
                    let key;

                    if (view === "Monthly") {
                        key = `${invoiceDate.getFullYear()}-${invoiceDate.getMonth() + 1}`;
                    } else if (view === "Weekly") {
                        key = `Week ${getWeekNumber(invoiceDate)} - ${invoiceDate.getFullYear()}`;
                    } else if (view === "Daily") {
                        key = invoiceDate.toISOString().split("T")[0];
                    }

                    groupedData[key] = (groupedData[key] || 0) + parseFloat(invoice.final_amount);
                }
            });

            const formattedData = Object.keys(groupedData).map((key) => ({
                date: key,
                totalSales: groupedData[key],
            }));

            setSalesData(formattedData);
        } catch (error) {
            console.error("Error fetching sales data:", error);
        }
    };

    const getWeekNumber = (date) => {
        const firstDay = new Date(date.getFullYear(), 0, 1);
        const dayOfYear = ((date - firstDay + 86400000) / 86400000);
        return Math.ceil(dayOfYear / 7);
    };

    const toggleView = () => {
        const views = ["Daily", "Weekly", "Monthly"];
        const currentIndex = views.indexOf(view);
        setView(views[(currentIndex + 1) % views.length]);
    };

    return (
        <Section>
            <div className="sales">
                <div className="sales__details">
                    <h4>Sales</h4>
                    <button onClick={toggleView}>
                        {view} <AiOutlineCaretDown />
                    </button>
                </div>
                <div className="sales__graph">
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart
                            data={salesData}
                            margin={{ top: 5, right: 20, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" fontSize={10} />
                            <YAxis fontSize={10} />
                            <Tooltip cursor={{ stroke: "#8884d8", strokeWidth: 2 }} />
                            <CartesianGrid strokeDasharray="3 3" />
                            <Area
                                type="monotone"
                                dataKey="totalSales"
                                stroke="#8884d8"
                                fillOpacity={1}
                                fill="url(#salesGradient)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Section>
    );
}

export default Sales;

// ✅ Styled Components
const Section = styled.section`
    .sales {
        margin-top: 10px;
        padding: 1rem;
        background-color: #f7f9fc;
        border-radius: 0.75rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        .sales__details {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;

            h4 {
                font-weight: bold;
                font-size: 1rem;
                color: #333;
            }

            button {
                background-color: #668dff;
                color: white;
                padding: 0.4rem 0.8rem;
                border: none;
                border-radius: 0.4rem;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 0.2rem;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                font-size: 0.75rem;
                transition: background 0.3s;

                &:hover {
                    background-color: #4a6ed1;
                }

                svg {
                    font-size: 0.6rem;
                }
            }
        }

        .sales__graph {
            height: 200px;
            width: 100%;
            border-radius: 0.75rem;
            background-color: #fff;
            padding: 0.75rem;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

            .recharts-default-tooltip {
                background-color: #333 !important;
                color: white !important;
                border-radius: 0.4rem;
                padding: 0.4rem;
            }
        }
    }
`;
